import {
  CRITERIA_REVENUE_MIN,
  CRITERIA_ANY,
} from "@/constants/sub_role_criteria_types";

/**
 * A frase que nomeia o que falta para o próximo subnível — o item 15 do card do Plano de
 * Carreira ("Você está a R$ 3.200 do próximo marco da sua carreira").
 *
 * Devolve pedaços em vez de uma string pronta para o valor que falta sair em destaque na tela,
 * e `quiet` para os estados que não são uma cobrança (topo da escada, fora do plano).
 *
 * Fica fora do componente de propósito: é a parte com regra de negócio, e é a que o QA reabre.
 */

const isRevenue = (item) => item && item.type === CRITERIA_REVENUE_MIN;

const plain = (text, sub) => ({ parts: [{ text }], sub: sub || null, quiet: false });
const quiet = (text) => ({ parts: [{ text }], sub: null, quiet: true });

const subRoleName = (subRole) =>
  subRole ? subRole.commercial_label || subRole.label : "";

const missingAmount = (item) =>
  Math.max(0, (item.target || 0) - (item.current || 0));

function amountPhrase(item, target, sub, formatCurrency) {
  return {
    parts: [
      { text: "Faltam " },
      { text: formatCurrency(missingAmount(item)), strong: true },
      { text: ` para alcançar ${target}.` },
    ],
    sub: sub || null,
    quiet: false,
  };
}

function firstRevenueOption(group) {
  for (const option of group.options || []) {
    const found = (option.items || []).find(isRevenue);
    if (found) return found;
  }
  return null;
}

/**
 * Tudo que falta no caminho alternativo, junto -- nao so o primeiro.
 *
 * PNC 1 aceita "R$ 50.000 OU (Certificado Hunter E Certificado Closer)". Citando so o primeiro
 * pendente, a frase virava "ou a certificacao Hunter" e sugeria que um certificado bastava,
 * quando a matriz exige os dois.
 */
function firstNamedOption(group) {
  for (const option of group.options || []) {
    const pending = (option.items || [])
      .filter((item) => !item.satisfied && item.gapLabel)
      .map((item) => item.gapLabel);

    if (pending.length) return pending.join(" e ");
  }
  return null;
}

/**
 * @param {object}   progress        payload de /career/sub_role_progress
 * @param {Array}    checklist       next_items já passados por describeCriteriaItem
 * @param {Function} formatCurrency  formatador de moeda da aplicação
 */
export function buildCareerGap(progress, checklist, formatCurrency) {
  if (!progress) return quiet("");

  if (!progress.actuation_key) {
    return quiet("Não faz parte do plano de carreira.");
  }

  const current = progress.current;
  const next = progress.next;
  const sub = current ? `Subnível atual: ${subRoleName(current)}` : null;

  if (!next) {
    // Sem proximo E sem atual significa catalogo vazio, nao topo da escada: dizer que alcancou
    // o ultimo subnivel seria mentira para quem nao tem nenhum.
    return quiet(
      current
        ? "Último subnível desta atuação alcançado."
        : "Esta atuação ainda não tem subníveis definidos."
    );
  }

  const nextName = subRoleName(next);

  // O card recalcula ao vivo; a ficha lê o que a rotina diária gravou. Entre uma coisa e outra
  // o PN cumpriu o critério e ainda não subiu -- e precisa saber disso.
  if (progress.pending_sync && current) {
    return plain(`Requisitos de ${nextName} cumpridos.`, sub);
  }

  const items = checklist || [];
  const pending = items.filter((item) => !item.satisfied);

  if (!pending.length) {
    return plain(`Requisitos de ${nextName} cumpridos.`, sub);
  }

  if (pending.length > 1) {
    return plain(
      `Faltam ${pending.length} requisitos para alcançar ${nextName}.`,
      sub
    );
  }

  const only = pending[0];

  // Alternativas da matriz ("R$ 50.000 OU Certificado Hunter e Closer"): cita o caminho que tem
  // número e lembra que existe outro, em vez de cobrar os dois.
  if (only.type === CRITERIA_ANY) {
    const withAmount = firstRevenueOption(only);
    const other = firstNamedOption(only);

    if (withAmount && other) {
      return amountPhrase(withAmount, `${nextName} — ou ${other}`, sub, formatCurrency);
    }
    if (withAmount) {
      return amountPhrase(withAmount, nextName, sub, formatCurrency);
    }
    if (other) {
      return plain(`Falta ${other} para alcançar ${nextName}.`, sub);
    }
  }

  if (isRevenue(only)) {
    return amountPhrase(only, nextName, sub, formatCurrency);
  }

  // Faturamento já cumprido e sobrou só requisito não numérico: vale dizer que a parte
  // comercial está fechada, que é o segundo exemplo do item 15.
  const hadRevenue = items.some(isRevenue);
  const prefix = hadRevenue ? "Requisitos comerciais concluídos. " : "";

  return plain(
    `${prefix}Falta ${only.gapLabel || only.label} para alcançar ${nextName}.`,
    sub
  );
}
